import React, {memo, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {useSelector} from 'react-redux';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useLocalization} from "../../../../hooks/localization"
import {useRPC} from "../../../../hooks/rpc"
import { useMessage } from '../../../../hooks/message';
import { useHistory } from 'react-router-dom';
interface VersionItem {
  id: string;
  deviceTypeId: string;
  deviceName: string;
  deviceTypeName: string;
  version: string;
  description: string;
  error_level: 'green' | 'yellow' | 'red';
  created_timestamp: number;
  updated_timestamp: number;
  last_local_update_timestamp?: string;
}

export const DeviceVersionListScreen = memo(function DeviceVersionListScreen() {
    const { t } = useLocalization()
    const [rpc] = useRPC();
    const {failure, success} = useMessage(); 
    const history = useHistory();
    
    const deviceVersionList = useSelector(state => state.control.deviceVersionList);

    const [selectedVersion, setSelectedVersion] = useState<VersionItem | null>(null);
    
    const bridgeDeviceTypes = useSelector(state => state.control.bridgeDeviceTypeList);

    const onVersionUpdate = useCallback(() => {
      rpc.UpdateDeviceVersionsInfo()
        .then(() => {
          success(t('version_update_success'));
        })
        .catch((error) => {
          rpc.frontendErrorLog(error)
          failure(error);
        });
    }, [t, rpc, success, failure,]);

    const allVersions: VersionItem[] = useMemo(() => {
      if (!deviceVersionList?.device_type_id) return [];

      const mergedVersionsMap = new Map<string, VersionItem>();

      const pad = (n: number): string => n.toString().padStart(2, '0');
      const formatTimestamp = (timestamp: number): string => {
        const date = new Date(timestamp * 1000);
        return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
      };

      Object.entries(deviceVersionList.device_type_id).forEach(([deviceTypeId, versionList]) => {
        const latestVersion = versionList[0];
        const deviceName = latestVersion?.device_name || 'Unknown';
        const deviceTypeInfo = bridgeDeviceTypes.find(d => d.id === deviceTypeId);
        const deviceTypeName = deviceTypeInfo?.internal_type_id?.toString() || 'N/A';

        if (!mergedVersionsMap.has(deviceName) && latestVersion) {
          mergedVersionsMap.set(deviceName, {
            ...latestVersion,
            deviceTypeId,
            deviceName,
            deviceTypeName,
            last_local_update_timestamp: formatTimestamp(deviceVersionList.last_local_update_timestamp),
        });
        }
      });

      return Array.from(mergedVersionsMap.values());
    }, [deviceVersionList, bridgeDeviceTypes]);
    
    const buttons = useMemo(() => [
        {
            text: t('Update'),
            className: 'green',
            onClick: onVersionUpdate,
        },
        
    ], [ onVersionUpdate, t]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('device'),
            key: 'deviceName',
          },
          {
            length: 4,
            name: t('last_local_update_timestamp'),
            key: 'last_local_update_timestamp',
          }
          
        ], [t]);
    return (
            <PasswordContainer requiredRole="service" isFullscreen={true}>
                <SettingsPageContainer
                 buttons={buttons}
                 className="device-list-page"
                 backUrl={'/settings/device'}
                 searchable={true}>
                 <SettingsList
                    columns={columns}
                    items={allVersions}
                    listType="deviceVersionList"
                    keyExtractor={(item) => item.id}
                    selectedItem={selectedVersion}
                    onItemSelected={(item) => {
                      setSelectedVersion(item as VersionItem);
                      history.push(`/settings/device/deviceVersionList/${item.deviceTypeId}`);
                    }}/>
                </SettingsPageContainer>
            </PasswordContainer>
        );
})